import { useState, type ReactNode } from "react";
import { type useTranslate } from "@refinedev/core";

import { CeIcon } from "./CeIcon";
import "./commEditorShell.css";

// De schil van de sjablooneditor, overgenomen uit de Jmix-bron (comm-editor.js#_render): de kopbalk
// met badges en acties, de vier tabbladen, de kolomindeling en de voetbalk. De inhoud van elke kolom
// komt van de aanroeper — die weet wat er in de editor staat; deze weet hoe het scherm eruitziet.
//
// Waarom een aparte schil: de editor zelf gaat over opslaan, publiceren, valideren en AI. Dat is al
// een vol bestand. Door de vorm hier te houden blijft beide leesbaar, en kun je de opmaak naast de
// bron leggen zonder door de logica heen te moeten.

type Translate = ReturnType<typeof useTranslate>;

/** Welke van de vier tabbladen open staat. */
export type EditorPaneel = "editor" | "voorbeeld" | "instellingen" | "geschiedenis";

export interface KopActie {
  label: string;
  onClick: () => void;
  /** Uit als de actie er (nog) niet is; de reden komt in de tooltip. */
  disabled?: boolean;
  titel?: string;
}

export function CommEditorShell({
  titel,
  ondertitel,
  statusBadge,
  herkomstBadge,
  laatstOpgeslagen,
  paneel,
  onPaneel,
  meerActies,
  onVoorbeeld,
  onPubliceren,
  publicerenUit,
  onSluiten,
  links,
  midden,
  rechts,
  ai,
  voorbeeld,
  instellingen,
  geschiedenis,
  voetLinks,
  voetActies,
  children,
  t,
}: {
  titel: string;
  ondertitel: string;
  statusBadge: string;
  herkomstBadge: string;
  /** "Laatst opgeslagen: …" — leeg als er nog nooit is opgeslagen. */
  laatstOpgeslagen: string | null;
  paneel: EditorPaneel;
  onPaneel: (p: EditorPaneel) => void;
  /** De inhoud van het menu "Meer acties"; leeg = geen menu. */
  meerActies: KopActie[];
  onVoorbeeld: () => void;
  onPubliceren: () => void;
  publicerenUit?: boolean;
  onSluiten: () => void;
  links: ReactNode;
  midden: ReactNode;
  rechts: ReactNode;
  /** De AI-kolom; null = geen AI, en dan is de indeling drie kolommen breed zoals in de bron. */
  ai: ReactNode | null;
  /** Het voorbeeld met testgegevens (tabblad Voorbeeld). */
  voorbeeld: ReactNode;
  /** Afdruk- en e-mailinstellingen, inclusief het briefpapier (tabblad Instellingen). */
  instellingen: ReactNode;
  /** De versiegeschiedenis van dit sjabloon (tabblad Geschiedenis). */
  geschiedenis: ReactNode;
  voetLinks: ReactNode;
  voetActies: ReactNode;
  /** Dialogen en dergelijke: ze horen bij de editor maar staan buiten de kolommen. */
  children?: ReactNode;
  t: Translate;
}) {
  // De sjabloongegevens staan dicht tot je ze nodig hebt; het rechterpaneel begint bij de velden,
  // want daar ga je als eerste heen.
  const [toonLinks, setToonLinks] = useState(false);
  const [rechtsTab, setRechtsTab] = useState<"invoegen" | "ai">("invoegen");

  const tabs: { key: EditorPaneel; label: string }[] = [
    { key: "editor", label: t("communicatieEditor.pane.editor") },
    { key: "voorbeeld", label: t("communicatieEditor.pane.preview") },
    { key: "instellingen", label: t("communicatieEditor.pane.settings") },
    { key: "geschiedenis", label: t("communicatieEditor.pane.history") },
  ];

  return (
    <div className="ce-root">
      <div className="ce-header">
        <div className="ce-h-title">
          <CeIcon naam="doc" />
          <div>
            <div className="ce-h-titlerow">
              <span className="ce-h-ttl">{titel}</span>
              <span className="ce-badge green sm">
                <CeIcon naam="check" />
                {statusBadge}
              </span>
              <span className="ce-badge grey sm">{herkomstBadge}</span>
            </div>
            <div className="ce-h-sub">{ondertitel}</div>
          </div>
        </div>

        {laatstOpgeslagen && (
          <div className="ce-h-status">
            <span className="ce-ok">
              <CeIcon naam="check" />
              {laatstOpgeslagen}
            </span>
          </div>
        )}

        <div className="ce-h-actions">
          {meerActies.length > 0 && (
            /* De bron heeft hier een uitklapmenu. Een <details> doet hetzelfde zonder eigen
               open/dicht-toestand, en sluit vanzelf bij Escape. */
            <details className="ce-menu">
              <summary className="ce-btn">
                {t("communicatieEditor.action.more")}
                <CeIcon naam="chevdown" />
              </summary>
              <div className="ce-menu-items" role="menu">
                {meerActies.map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    role="menuitem"
                    disabled={a.disabled}
                    title={a.titel}
                    onClick={a.onClick}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </details>
          )}
          <button type="button" className="ce-btn" onClick={onVoorbeeld}>
            <CeIcon naam="eye" />
            {t("communicatieEditor.pane.preview")}
          </button>
          <button type="button" className="ce-btn ce-primary" onClick={onPubliceren} disabled={publicerenUit}>
            <CeIcon naam="send" />
            {t("communicatieEditor.action.publish")}
          </button>
          <button
            type="button"
            className="ce-iconbtn"
            title={t("common.close")}
            aria-label={t("common.close")}
            onClick={onSluiten}
          >
            <CeIcon naam="close" />
          </button>
        </div>
      </div>

      <div className="ce-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ce-tab${tab.key === paneel ? " active" : ""}`}
            aria-current={tab.key === paneel}
            onClick={() => onPaneel(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="ce-body">
        {/* Alleen het open tabblad staat in de indeling; met display:contents blijven de kolommen
            kinderen van het raster. */}
        <div className={`ce-pane${paneel === "editor" ? " active" : ""}`}>
          {/* De sjabloongegevens zitten achter één knop: je stelt ze één keer in en kijkt er daarna
              zelden naar, terwijl ze wel een hele kolom kostten. */}
          <div className="ce-rail">
            <button
              type="button"
              className={`ce-rail-btn${toonLinks ? " on" : ""}`}
              aria-pressed={toonLinks}
              aria-expanded={toonLinks}
              title={t("communicatieEditor.settings.title")}
              onClick={() => setToonLinks((x) => !x)}
            >
              <CeIcon naam="settings" />
              <span>{t("communicatieEditor.rail.template")}</span>
            </button>
          </div>
          {toonLinks && <div className="ce-col">{links}</div>}
          <div className="ce-center">{midden}</div>
          <div className="ce-right">
            <div className="ce-card ce-rechtkaart">
              <div className="ce-rtabs">
                <button
                  type="button"
                  className={`ce-rtab${rechtsTab === "invoegen" ? " active" : ""}`}
                  onClick={() => setRechtsTab("invoegen")}
                >
                  <CeIcon naam="variable" />
                  {t("communicatieEditor.insert.title")}
                </button>
                {ai && (
                  <button
                    type="button"
                    className={`ce-rtab${rechtsTab === "ai" ? " active" : ""}`}
                    onClick={() => setRechtsTab("ai")}
                  >
                    <CeIcon naam="ai" />
                    {t("communicatieAi.title")}
                  </button>
                )}
              </div>
              <div className="ce-rechtinhoud">{rechtsTab === "ai" && ai ? ai : rechts}</div>
            </div>
          </div>
        </div>
        {paneel !== "editor" && (
          <div className="ce-pane active">
            <div className="ce-simple">
              {paneel === "voorbeeld" && voorbeeld}
              {paneel === "instellingen" && instellingen}
              {paneel === "geschiedenis" && geschiedenis}
            </div>
          </div>
        )}
      </div>

      <div className="ce-bottom">
        <div className="ce-b-left">{voetLinks}</div>
        <div className="ce-b-actions">{voetActies}</div>
      </div>
      {children}
    </div>
  );
}

